import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Whisper, Tooltip, Popover, Button } from "rsuite";
import { RiListUnordered } from "react-icons/ri";
import { FiSearch, FiX, FiChevronDown } from "react-icons/fi";
import BillingViewFilters from "../../BillingFilterJSON";
import DefaultPatientFilters from "../ClinicComponent/Filters/DefaultPatientFilters";
import AddDistributorModel from "../ClinicModal/AddDistributorModel";
import { usePostFilteredPurchaseMutation } from "../../State/Api/PharmacyOrderApiSlice";
import PharmacyPurchase1Table from "../pharmacy/PharmacyTables/Pharmacypurchase1Table";

function PharmacyPurchase1() {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("list");
  const [patientDetail, setPatientDetail] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [hasData, setHasData] = useState(true);
  const [open, setOpen] = useState(false);
  const whisperRef = useRef(null);
  const gridRef = useRef(null);

  const [postFilteredPharmacyPurchase, { isLoading, isFetching, isSuccess }] =
    usePostFilteredPurchaseMutation();

  const handlePurchaseOrder = () => {
    navigate("/PurchaseOrder");
  };

  const handlePharmacyPurchasereturn = () => {
    navigate("/PharmacyPurchasereturn", {
      state: { openModal: true },
    });
  };

  const handleAddDistributor = () => {
    if (whisperRef.current) {
      whisperRef.current.close();
    }
    setOpen(true);
  };

  const getButtonClasses = (view) => {
    const isActive = activeView === view;
    return [
      "flex-1 flex items-center justify-center h-[36px] w-[50px] transition-all",
      isActive
        ? "bg-[#203342] text-white"
        : "bg-white text-[#203342] hover:bg-gray-100",
    ].join(" ");
  };

  // ✅ Handle backend-compatible filtering logic
  const handleSearch = () => {
  const searchValue = patientDetail.trim();

  if (!searchValue) {
    setSelectedFilters(prev => {
      const { distributorName, ...rest } = prev;
      return rest;
    });
    return;
  }

  const newFilter = {
    distributorName: {
      values: new Set([searchValue]),
      operator: "Contains",
    },
  };

  setSelectedFilters(prev => {
    const { distributorName, ...rest } = prev;
    return { ...rest, ...newFilter };
  });
};


const handleClearSearch = () => {
  setPatientDetail("");
  setSelectedFilters(prev => {
    const { distributorName, ...rest } = prev;
    return rest;
  });
  setHasData(true);
};


  // ✅ Build request for backend
  const pharmaid = localStorage.getItem("userUniqueID");

  const getPharmacyPurchase = async (pageNumber, filters, sortColumn = "", sortOrder = "") => {
  try {
    const result = await postFilteredPharmacyPurchase({
      pageNumber,
      pageSize: 100,
      pharmaid,
      filters,
      sortColumn: sortColumn,
      sortOrder: sortOrder
    }).unwrap();

    return result;
  } catch (err) {
    console.error("Error fetching doctors:", err);
    return [];
  }
};

  useEffect(() => {
    gridRef?.current?.api?.purgeInfiniteCache();
  }, [selectedFilters]);

  const actionPopover = (
    <Popover title="Actions">
      <div className="flex flex-col gap-2">
  {/* Add Purchase (Blue Outline) */}
  <button
    className="w-full px-4 py-2 border-1 border-green-600 text-green-600 rounded-md font-medium hover:bg-green-50 transition"
    onClick={handlePurchaseOrder}
  >
    + Add Purchase
  </button>

  {/* Purchase Return (Green Outline) */}
  <button
    className="w-full px-4 py-2 border-1 border-green-600 text-green-600 rounded-md font-medium hover:bg-green-50 transition"
    onClick={handlePharmacyPurchasereturn}
  >
    + Purchase Return
  </button>

  {/* Add Distributor (Green Outline) */}
  <button
    className="w-full px-4 py-2 border-1 border-green-600 text-green-600 rounded-md font-medium hover:bg-green-50 transition"
    onClick={handleAddDistributor}
  >
    + Add Distributor
  </button>
</div>
    </Popover>
  );

  return (
    <>
      <main style={{ width: "100%", height: "100%" }}>
        <div className="row">
          <div className="col-12 mb-3 border-b border-dashed border-gray-300 pb-2">
            <div className="flex flex-wrap justify-between items-center gap-2">
              <div>
                <h2 className="text-[24px] font-bold text-gray-900 leading-tight">
                  Purchase
                </h2>
              </div>

              <div className="flex flex-wrap items-center gap-2 justify-end">
                {/* 🔍 Search */}
                <div className="relative flex items-center h-[40px]">
                  <input
                    type="text"
                    className="w-[250px] h-full border border-[#7eb592] rounded-lg pl-4 pr-12 text-sm focus:outline-none focus:border-[#7eb592] focus:ring-2 focus:ring-[#7eb592]/20"
                    placeholder="Enter distributor name or ID"
                    value={patientDetail}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPatientDetail(value);
                      if (value.trim() === "") {
                        setSelectedFilters((prev) => {
                          const { distributorName, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  {patientDetail && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-10 flex items-center justify-center text-gray-500 hover:text-red-400"
                      style={{ height: "100%" }}
                    >
                      <FiX size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="absolute right-1 flex items-center justify-center w-[34px] h-[34px] rounded-md bg-[#7eb592] hover:bg-[#6aa57f] text-white shadow-sm transition"
                  >
                    <FiSearch size={16} />
                  </button>
                </div>

                {/* 🧩 Filters */}
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip>Filter Options</Tooltip>}
                >
                  <DefaultPatientFilters
                    defaultFilters={BillingViewFilters.gst}
                    additionalFilters={BillingViewFilters.additionalPurchase}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                    setHasData={setHasData}
                  />
                </Whisper>

                {/* ➕ Add Buttons */}
                <Whisper
                  placement="bottomEnd"
                  trigger="click"
                  speaker={actionPopover}
                  ref={whisperRef}
                >
                  <button
                    className="gradient-button px-4 py-2 rounded-lg text-white font-semibold flex items-center"
                    type="button"
                  >
                    Select Purchase
                    <FiChevronDown className="ml-2" />
                  </button>
                </Whisper>

                {/* 📋 View toggle */}
                {/* <div className="flex items-center rounded-md overflow-hidden shadow-sm border border-gray-200 w-fit ml-1">
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip>List view</Tooltip>}
                  >
                    <button
                      onClick={() => setActiveView("list")}
                      className={getButtonClasses("list")}
                    >
                      <RiListUnordered size={20} />
                    </button>
                  </Whisper>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Table */}
        {activeView === "list" && (
          <PharmacyPurchase1Table
            getPharmacyPurchase={getPharmacyPurchase}
            isFetching={isFetching}
            isLoading={isLoading}
            isSuccess={isSuccess}
            gridRef={gridRef}
            selectedFilters={selectedFilters}
            hasData={hasData}
            setHasData={setHasData}
          />
        )}

        {/* ➕ Distributor modal */}
        <AddDistributorModel isOpen={open} onClose={() => setOpen(false)} />
      </main>
    </>
  );
}

export default PharmacyPurchase1;
